
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Cog, Shield, AlertTriangle, Users, Clock, Wrench } from 'lucide-react';

const ManufacturingPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Helmet>
        <title>PEO Services for Manufacturing Companies | The PEO App</title>
        <meta name="description" content="Specialized PEO services for manufacturing companies. Expert workers' compensation, safety compliance, and manufacturing-specific HR solutions for production facilities." />
        <link rel="canonical" href="https://thepeoapp.com/industries/manufacturing" />
        <meta name="keywords" content="manufacturing PEO, factory PEO, production facility HR, manufacturing workers comp, industrial safety compliance, manufacturing payroll" />
        
        <meta property="og:title" content="PEO Services for Manufacturing Companies" />
        <meta property="og:description" content="Specialized PEO solutions for manufacturing with safety compliance and workers' compensation expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/manufacturing" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Cog className="h-16 w-16 text-gray-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Manufacturing Companies
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for manufacturing companies and production facilities. 
              Expert safety compliance, workers' compensation, and industrial HR solutions.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Manufacturing Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Safety Compliance</h3>
                    <p className="text-gray-700">OSHA regulations, machine safety, and hazardous material handling</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">High Workers' Comp Costs</h3>
                    <p className="text-gray-700">Manufacturing has elevated injury risk and workers' compensation rates</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Skilled Labor Shortage</h3>
                    <p className="text-gray-700">Difficulty finding and retaining qualified production workers</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Shift Work Management</h3>
                    <p className="text-gray-700">24/7 operations with complex scheduling and overtime management</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Wrench className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Training Requirements</h3>
                    <p className="text-gray-700">Ongoing safety training and certification management</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Cog className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Production Pressure</h3>
                    <p className="text-gray-700">Balancing productivity demands with safety and compliance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Manufacturing-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <Shield className="h-12 w-12 text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Safety Program Management</h3>
                <p className="text-gray-700">
                  Comprehensive OSHA compliance, safety training programs, 
                  and accident prevention specifically for manufacturing environments.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <AlertTriangle className="h-12 w-12 text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Workers' Comp Expertise</h3>
                <p className="text-gray-700">
                  Specialized workers' compensation programs with experience managing 
                  manufacturing claims and reducing modification rates.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <Clock className="h-12 w-12 text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Shift Work Support</h3>
                <p className="text-gray-700">
                  Complex scheduling, overtime management, and 24/7 HR support 
                  for continuous manufacturing operations.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Manufacturing-Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand manufacturing challenges 
              and have proven experience managing production facility workforces.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Manufacturing PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Manufacturing Types We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Automotive</h3>
                <p className="text-sm text-gray-600">Auto parts, assembly, suppliers</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Food & Beverage</h3>
                <p className="text-sm text-gray-600">Food processing, packaging, distribution</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Electronics</h3>
                <p className="text-sm text-gray-600">Circuit boards, components, assembly</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Textiles</h3>
                <p className="text-sm text-gray-600">Clothing, fabrics, industrial textiles</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ManufacturingPEO;
